/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CalendarBlank, Clock, CheckCircle, PaperPlaneTilt } from '@phosphor-icons/react';
import { API } from '@/config/api';

// Public booking page. Works in two modes:
//   1. /book/:leadId   — legacy per-lead links from older outreach emails
//   2. /call-appointment — single brandable URL for all new outreach
export default function BookCall() {
  const { leadId } = useParams();             // undefined on /call-appointment
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [error, setError] = useState('');
  // 30 visible dates starting today
  const [dates] = useState(() => {
    const out = [];
    const start = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      out.push(d);
    }
    return out;
  });
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company_name: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null); // { slot_local_label }

  // Public endpoints live under /api/admin/outreach/public to share the same router
  const PUBLIC_BASE = `${API}/admin/outreach/public`;
  const infoUrl = leadId ? `${PUBLIC_BASE}/booking-info/${leadId}` : `${PUBLIC_BASE}/booking-info`;
  const bookUrl = leadId ? `${PUBLIC_BASE}/book/${leadId}` : `${PUBLIC_BASE}/book`;

  useEffect(() => {
    let cancelled = false;
    setLoadingInfo(true);
    axios.get(infoUrl)
      .then(res => {
        if (cancelled) return;
         
        setInfo(res.data);
        // Pre-fill company name only when we have a real lead behind the URL
        if (res.data?.company_name && !res.data?.generic) {
           
          setForm(prev => ({ ...prev, company_name: res.data.company_name }));
        }
      })
      .catch(err => {
        if (cancelled) return;
         
        setError(err?.response?.data?.detail || 'This booking link is no longer active.');
      })
      .finally(() => { if (!cancelled) setLoadingInfo(false); });
    return () => { cancelled = true; };
  }, [infoUrl]);

  const fetchSlots = useCallback(async (dateStr) => {
    setLoadingSlots(true);
    setSelectedSlot(null);
    try {
      const params = { date: dateStr };
      if (leadId) params.lead_id = leadId;
      const res = await axios.get(`${PUBLIC_BASE}/available-slots`, { params });
      setSlots(res.data.slots || []);
    } catch (err) {
      setSlots([]);
      toast.error(err?.response?.data?.detail || 'Could not load slots');
    } finally {
      setLoadingSlots(false);
    }
  }, [PUBLIC_BASE, leadId]);

  useEffect(() => {
    if (selectedDate) fetchSlots(selectedDate);
  }, [selectedDate, fetchSlots]);

  const handleSubmit = async () => {
    if (!selectedSlot) { toast.error('Pick a time slot first'); return; }
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.company_name.trim()) {
      toast.error('Name, email, phone and company name are required'); return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(bookUrl, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company_name: form.company_name.trim(),
        notes: form.notes.trim(),
        slot_start: selectedSlot.start_utc,
      });
      setConfirmation(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not book this slot. Try another time.');
      // Re-fetch slots in case the slot just got taken
      fetchSlots(selectedDate);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingInfo) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <p className="text-gray-400 animate-pulse">Loading…</p>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 text-center">
        <p className="text-xl font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>Booking link expired</p>
        <p className="text-sm text-gray-500 max-w-md">{error}</p>
        <a href="/" className="mt-4 text-sm text-blue-700 hover:underline">Go to homepage</a>
      </div>
    );
  }
  if (confirmation) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-14 h-14 mx-auto bg-emerald-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle size={32} weight="fill" className="text-emerald-600" />
          </div>
          <h1 className="text-2xl text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>You're booked!</h1>
          <p className="text-sm text-gray-600 mb-4">Your 15-minute call with Study Alliance is set for:</p>
          <p className="text-base font-semibold text-gray-900 bg-blue-50 border border-blue-100 rounded-lg py-3 px-4" data-testid="booking-confirmation-slot">
            {confirmation.slot_local_label}
          </p>
          <div className="mt-3 text-xs text-gray-600 space-y-1">
            {confirmation.confirmed_email && (
              <p>Confirmation sent to <strong className="text-gray-800">{confirmation.confirmed_email}</strong></p>
            )}
            {confirmation.confirmed_phone && (
              <p>We'll call you at <strong className="text-gray-800">{confirmation.confirmed_phone}</strong></p>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-4">If anything looks off, just reply to the confirmation email to reschedule.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gradient-to-br from-blue-50/40 via-white to-white py-10 px-4">
      <div className="max-w-3xl mx-auto" data-testid="booking-page">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-widest text-blue-700 font-semibold mb-2">Study Alliance</p>
          <h1 className="text-3xl sm:text-4xl text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>Schedule a 15-min call</h1>
          <p className="text-sm text-gray-600 max-w-xl mx-auto">
            {info?.generic
              ? <>Pick a time that works for you. All times shown in <strong>{info?.timezone_label || 'Pacific Time'}</strong>.</>
              : <>Hi <span className="font-semibold">{info?.company_name || 'there'}</span> — pick a time that works for you. All times shown in <strong>{info?.timezone_label || 'Pacific Time'}</strong>.</>}
          </p>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {/* Step 1: pick a date */}
          <div className="border-b border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <CalendarBlank size={16} className="text-blue-700" />
              <p className="text-sm font-semibold text-gray-900">1. Pick a date</p>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-2 px-2" data-testid="date-strip">
              {dates.map(d => {
                const iso = d.toISOString().slice(0, 10);
                const isSelected = iso === selectedDate;
                return (
                  <button
                    key={iso}
                    onClick={() => setSelectedDate(iso)}
                    className={`flex-shrink-0 w-16 py-2 rounded-lg border text-center transition-all ${
                      isSelected ? 'bg-blue-700 text-white border-blue-700 shadow-sm' : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
                    }`}
                    data-testid={`date-${iso}`}
                  >
                    <p className={`text-[10px] uppercase tracking-wider font-semibold ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>{d.toLocaleDateString(undefined, { weekday: 'short' })}</p>
                    <p className="text-lg font-bold leading-tight">{d.getDate()}</p>
                    <p className={`text-[10px] ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>{d.toLocaleDateString(undefined, { month: 'short' })}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Step 2: pick a slot */}
          <div className="border-b border-gray-100 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock size={16} className="text-blue-700" />
              <p className="text-sm font-semibold text-gray-900">2. Pick a time</p>
            </div>
            {loadingSlots ? (
              <p className="text-sm text-gray-400 py-6 text-center">Loading slots…</p>
            ) : slots.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">No slots available on this day — try another date.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="slots-grid">
                {slots.map(s => {
                  const isSelected = selectedSlot?.start_utc === s.start_utc;
                  return (
                    <button
                      key={s.start_utc}
                      disabled={!s.available}
                      onClick={() => setSelectedSlot(s)}
                      className={`py-2 px-1 text-xs font-medium rounded-md border transition-all ${
                        !s.available
                          ? 'bg-gray-50 text-gray-300 border-gray-100 line-through cursor-not-allowed'
                          : isSelected
                            ? 'bg-blue-700 text-white border-blue-700 shadow-sm'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                      data-testid={`slot-${s.start_utc}`}
                    >
                      {s.local_label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 3: details */}
          {selectedSlot && (
            <div className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <PaperPlaneTilt size={16} className="text-blue-700" />
                <p className="text-sm font-semibold text-gray-900">3. Your details</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Full Name *</Label>
                  <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Your name" data-testid="booking-name" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Email *</Label>
                  <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="you@example.com" data-testid="booking-email" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Phone *</Label>
                  <Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+1 416 555 0123" data-testid="booking-phone" className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">Company Name *</Label>
                  <Input value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} placeholder="Your agency" data-testid="booking-company" className="mt-1" />
                </div>
                <div className="sm:col-span-2">
                  <Label className="text-xs">What would you like to discuss? (optional)</Label>
                  <textarea
                    value={form.notes}
                    onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                    rows={3}
                    placeholder="Anything you want us to know ahead of the call"
                    className="mt-1 w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 font-sans"
                    data-testid="booking-notes"
                  />
                </div>
              </div>
              <div className="mt-5 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-gray-500">
                  <strong>Confirmed time:</strong> {selectedSlot.local_label} on {new Date(selectedDate).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                <Button onClick={handleSubmit} disabled={submitting} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid="booking-submit">
                  {submitting ? 'Booking…' : 'Confirm Booking'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
